<template>
    <v-card :loading="loading">
        <v-card-title>
            <v-btn small icon @click="close" color="primary" class="mr-2">
                <v-icon>
                    mdi-close
                </v-icon>
            </v-btn>
            <h3 class="text-capitalize">{{ title }}</h3>
        </v-card-title>
        <v-divider class="ml-4 mr-5" />
        <v-card-text>
            <v-row no-gutters>
                <v-col cols="12">
                    <v-text-field
                        v-model="item.name"
                        prepend-icon="mdi-text-short"
                        label="Name"
                        :rules="[rules.required]"
                    >
                    </v-text-field>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col cols="12">
                    <div class="mt-4">
                        <label
                            class="v-label theme--light"
                            style="font-size: 12px; color: rgba(0, 0, 0, 0.6);"
                        >
                            Description
                        </label>
                        <vue-editor
                            ref="descriptionEditor"
                            v-model="item.description"
                            :editor-options="editorOptions"
                            :style="{ height: '300px' }"
                            @ready="setupPasteHandler"
                        />
                    </div>
                </v-col>
            </v-row>
        </v-card-text>
        <v-card-actions>
            <v-spacer />
            <v-btn
                color="primary"
                class="mb-4"
                @click="saveItem"
                rounded
                :loading="loading"
            >
                SAVE
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import { objDiff } from '@/helpers/objDiff.js'
import { VueEditor } from 'vue2-editor'

export default {
    name: 'ProductTypeForm',
    components: {
        VueEditor,
    },
    props: {
        item: {
            type: Object,
            default: () => ({}),
        },
        originalItem: {
            type: Object,
            default: () => ({}),
        },
        createForm: {
            type: Boolean,
            default: () => false,
        },
        quoteSettings: {
            type: Object,
            default: () => ({}),
        },
        title: {
            type: String,
            default: () => '',
        },
    },
    data() {
        return {
            loading: false,
            rules: {
                required: v => !!v || 'The value is required!',
            },
            editorOptions: {
                modules: {
                    toolbar: [
                        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [
                            { align: '' },
                            { align: 'center' },
                            { align: 'right' },
                        ],
                        ['blockquote'],
                        [
                            { list: 'ordered' },
                            { list: 'bullet' },
                            { list: 'check' },
                        ],
                        [{ indent: '-1' }, { indent: '+1' }],
                        [{ color: [] }, { background: [] }],
                    ],
                },
            },
        }
    },
    computed: {
        itemDiff: function() {
            if (!this.createForm) {
                return objDiff(this.originalItem, this.item)
            } else {
                return null
            }
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        async saveItem() {
            if (this.createForm) {
                await this.create()
            } else {
                await this.update()
            }
        },
        async create() {
            try {
                this.loading = true
                const productType = await API.createQuoteProductType({
                    settingId: this.quoteSettings.id,
                    name: this.item.name,
                    description: this.item.description,
                })
                this.$emit('addItem', productType)
                this.$emit('close')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async update() {
            try {
                this.loading = true
                const productType = await API.updateQuoteProductType({
                    settingId: this.quoteSettings.id,
                    itemId: this.item.id,
                    paramsToUpdate: this.itemDiff,
                })
                const item = { id: this.item.id, ...productType }
                this.$emit('replaceItem', item)
                this.$emit('close')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        close() {
            this.$emit('close')
        },
        setupPasteHandler(quill) {
            if (quill._pasteHandlerSetup) return
            quill._pasteHandlerSetup = true

            const Delta = quill.constructor.import('delta')

            quill.root.addEventListener(
                'paste',
                e => {
                    const clipboardData = e.clipboardData || window.clipboardData
                    if (!clipboardData) return

                    const pastedHTML = clipboardData.getData('text/html')
                    const pastedText = clipboardData.getData('text/plain')

                    if (!pastedText) return

                    // Only process if it has bullet points
                    if (!/^[\u2022\u2023\u25E6\u2043\u2219\-*]\s+/m.test(pastedText)) return

                    e.preventDefault()
                    e.stopPropagation()
                    e.stopImmediatePropagation()

                    const range = quill.getSelection(true)
                    let newDelta = new Delta().retain(range.index)
                    const lines = pastedText.split('\n')
                    const tempDiv = document.createElement('div')
                    if (pastedHTML) tempDiv.innerHTML = pastedHTML

                    // Helper function to get HTML element
                    const getHtmlElement = text => {
                        const elements = tempDiv.querySelectorAll('p, div, li')
                        for (const el of elements) {
                            if (el.textContent.trim() === text) return el
                        }
                        return null
                    }

                    // Helper function to preserve HTML formatting (without list format)
                    const insertWithFormat = (text, htmlElement) => {
                        if (htmlElement && pastedHTML) {
                            const delta = quill.clipboard.convert(htmlElement.outerHTML)
                            delta.ops.forEach(op => {
                                if (typeof op.insert === 'string' && op.insert !== '\n') {
                                    const attrs = { ...(op.attributes || {}) }
                                    delete attrs.list
                                    newDelta.insert(
                                        op.insert,
                                        Object.keys(attrs).length > 0 ? attrs : undefined
                                    )
                                }
                            })
                        } else {
                            newDelta.insert(text)
                        }
                    }

                    lines.forEach((line, index) => {
                        const trimmed = line.trim()
                        const nextLine = index < lines.length - 1 ? lines[index + 1] : null
                        const hasEmptyLineAfter = nextLine !== null && !nextLine.trim()

                        // Empty line
                        if (!trimmed) {
                            if (hasEmptyLineAfter || (index < lines.length - 1 && !nextLine.trim())) {
                                newDelta.insert('\n')
                            }
                            return
                        }

                        // Detect bullet point
                        const bulletMatch = trimmed.match(/^[\u2022\u2023\u25E6\u2043\u2219\-*]\s+(.+)$/)

                        if (bulletMatch) {
                            // It's a bullet - remove bullet from HTML and preserve formatting
                            const contentText = bulletMatch[1]
                            const htmlElement = getHtmlElement(trimmed)

                            if (htmlElement && pastedHTML) {
                                const walker = document.createTreeWalker(
                                    htmlElement,
                                    NodeFilter.SHOW_TEXT,
                                    null
                                )
                                const textNode = walker.nextNode()
                                if (textNode) {
                                    const original = textNode.textContent
                                    const match = original.match(/^([\u2022\u2023\u25E6\u2043\u2219\-*]\s*)/)
                                    if (match) {
                                        textNode.textContent = original.substring(match[1].length)
                                    }

                                    const delta = quill.clipboard.convert(htmlElement.outerHTML)
                                    delta.ops.forEach(op => {
                                        if (typeof op.insert === 'string' && op.insert !== '\n') {
                                            newDelta.insert(op.insert, op.attributes || {})
                                        }
                                    })

                                    textNode.textContent = original
                                }
                            } else {
                                newDelta.insert(contentText)
                            }

                            newDelta.insert('\n', { list: 'bullet' })
                        } else {
                            // Not a bullet
                            insertWithFormat(trimmed, getHtmlElement(trimmed))
                            
                            // Only add line break if there's an empty line after
                            if (hasEmptyLineAfter) {
                                newDelta.insert('\n')
                            }
                        }
                    })

                    quill.updateContents(newDelta, 'user')
                    quill.setSelection(range.index + newDelta.length())
                },
                true
            )
        },
    },
}
</script>

<style scoped>
.quillWrapper >>> .ql-toolbar {
    border: none;
    border-bottom: thin solid rgba(0, 0, 0, 0.12);
}
.quillWrapper >>> .ql-container {
    border: none;
}
</style>
